import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { INVOICE_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import { InvoiceStateModel } from '~backoffice/app/~shared/models/store.models';
import {
  CalculateInvoiceAmounts,
  InitCertifiedInvoice,
  InitDraftInvoice,
  PatchDraftInvoice,
  PatchInvoiceLine,
  RemoveInvoiceLine,
  ResetState
} from '~backoffice/app/~store/actions/invoice.actions';
import { CreateInvoiceParams, LineItem } from '~core/models/invoice.models';
import { ValidationTransactionService } from '~core/services/validation-transaction.service';
import { buildComputationParams } from '~backoffice/app/~shared/utils/invoice';
import { InvoiceService } from '~core/services/invoice.service';
import { ToastService } from '~backoffice/app/~shared/services/toast.service';
import { getErrorMessage } from '~backoffice/app/~shared/utils/utils';

export const DEFAULT_INVOICE_STATE: InvoiceStateModel = {
  draft: {
    invoice: undefined,
    certifiedInvoice: undefined,
    currentLineItem: undefined,
    invoiceReferenceNumber: undefined,
    invoiceCreationError: undefined,
    invoiceCertificationError: undefined
  },
  operationsError: undefined
};

@State({
  name: INVOICE_STATE_TOKEN,
  defaults: DEFAULT_INVOICE_STATE
})
@Injectable()
export class InvoiceState {
  private _validationService = inject(ValidationTransactionService);
  private _invoiceService = inject(InvoiceService);
  private _toastService = inject(ToastService);

  @Action(ResetState, { cancelUncompleted: true })
  resetState(ctx: StateContext<InvoiceStateModel>, action: ResetState): void {
    ctx.setState({
      ...DEFAULT_INVOICE_STATE,
      ...action.data
    });
  }

  @Action(InitDraftInvoice, { cancelUncompleted: true })
  initDraftInvoice(
    ctx: StateContext<InvoiceStateModel>,
    action: InitDraftInvoice
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();

    ctx.patchState({
      draft: {
        ...draft,
        invoice: {
          ...draft.invoice,
          ...action.data
        }
      }
    });
  }

  @Action(InitCertifiedInvoice, { cancelUncompleted: true })
  initCertifiedInvoice(
    ctx: StateContext<InvoiceStateModel>,
    action: InitCertifiedInvoice
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();

    ctx.patchState({
      draft: {
        ...draft,
        certifiedInvoice: {
          ...draft.certifiedInvoice,
          ...action.data
        }
      }
    });
  }

  @Action(PatchDraftInvoice, { cancelUncompleted: true })
  patchDraftInvoice(
    ctx: StateContext<InvoiceStateModel>,
    action: PatchDraftInvoice
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();

    if (!draft.invoice) {
      return;
    }

    ctx.patchState({
      draft: {
        ...draft,
        invoice: {
          ...draft.invoice,
          ...action.data
        }
      }
    });
  }

  @Action(PatchInvoiceLine, { cancelUncompleted: true })
  patchInvoiceLine(
    ctx: StateContext<InvoiceStateModel>,
    action: PatchInvoiceLine
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();
    if (!draft.invoice) {
      return;
    }
    const currentLines = draft.invoice.lineItems || [];

    const existingIndex = currentLines.findIndex(line =>
      action.data.id
        ? line.id === action.data.id
        : line.article.name === action.data.article.name
    );

    let updatedLines;

    if (existingIndex !== -1) {
      updatedLines = currentLines.map((line, index) =>
        index === existingIndex ? { ...line, ...action.data } : line
      );
    } else {
      updatedLines = [...currentLines, action.data];
    }

    if (action.data.disbursed) {
      ctx.patchState({
        draft: {
          ...draft,
          invoice: {
            ...draft.invoice,
            lineItems: updatedLines
          }
        }
      });
      return;
    }

    ctx.dispatch(new CalculateInvoiceAmounts(updatedLines));
  }

  @Action(RemoveInvoiceLine, { cancelUncompleted: true })
  removeInvoiceLine(
    ctx: StateContext<InvoiceStateModel>,
    action: RemoveInvoiceLine
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();

    if (!draft.invoice) {
      return;
    }

    const currentLines = draft.invoice.lineItems || [];
    const targetIndex =
      typeof action.data === 'number'
        ? action.data
        : currentLines.findIndex(line => line.id === action.data);
    const targetLine = currentLines[targetIndex];

    const newLineItems =
      currentLines.filter(
        (_: LineItem, index: number) => index !== targetIndex
      ) || [];

    if (targetLine.disbursed) {
      ctx.patchState({
        draft: {
          ...draft,
          invoice: {
            ...draft.invoice,
            lineItems: newLineItems
          }
        }
      });
      return;
    }

    const nonDisbursedRemaining = newLineItems.filter(
      line => !line.disbursed
    ).length;

    if (nonDisbursedRemaining === 0) {
      const invoice = draft.invoice;
      const currency =
        invoice.amountBeforeTax?.currency ||
        invoice.totalAmount?.currency ||
        invoice.customer.billingCurrency ||
        'XOF';

      return this._invoiceService
        .update(invoice.id, {
          amountBeforeTax: { amount: 0, currency },
          totalAmount: { amount: 0, currency },
          discount: { amount: 0, currency },
          taxesApplied: []
        } as Partial<CreateInvoiceParams>)
        .pipe(
          tap(updatedInvoice => {
            ctx.patchState({
              draft: {
                ...draft,
                invoice: {
                  ...invoice,
                  ...updatedInvoice,
                  lineItems: newLineItems
                }
              }
            });
          })
        );
    }

    ctx.dispatch(new CalculateInvoiceAmounts(newLineItems));
  }

  @Action(CalculateInvoiceAmounts, { cancelUncompleted: true })
  calculateInvoiceAmounts(
    ctx: StateContext<InvoiceStateModel>,
    action: CalculateInvoiceAmounts
  ): void | Observable<unknown> {
    const { draft } = ctx.getState();
    const invoice = draft.invoice;
    if (!invoice || !action.lineItems) {
      ctx.patchState({
        draft: {
          ...draft
        }
      });
      return;
    }

    const nonDisbursedLineItems = action.lineItems.filter(
      line => !line.disbursed
    );

    if (!nonDisbursedLineItems.length) {
      ctx.patchState({
        draft: {
          ...draft,
          invoice: {
            ...invoice,
            lineItems: action.lineItems
          }
        }
      });
      return;
    }

    const params = buildComputationParams(nonDisbursedLineItems, invoice);

    return this._validationService.compute(params).pipe(
      switchMap(computationResults => {
        const {
          lineItems,
          paymentMethods,
          taxAmount,
          typeDocument,
          ...invoiceAmounts
        } = computationResults;

        return this._invoiceService
          .update(invoice.id, {
            ...invoiceAmounts
          } as Partial<CreateInvoiceParams>)
          .pipe(
            tap(updatedInvoice => {
              ctx.patchState({
                draft: {
                  ...draft,
                  invoice: { ...invoice, ...updatedInvoice }
                }
              });
            })
          );
      }),
      catchError(error => {
        this._toastService.open({
          content: getErrorMessage(error),
          type: 'error'
        });

        ctx.patchState({
          draft: {
            ...draft
          },
          operationsError: error
        });
        return of(null);
      })
    );
  }

  // selectors

  @Selector()
  static draft(
    state: InvoiceStateModel
  ): Pick<InvoiceStateModel, 'draft'>['draft'] {
    return state.draft;
  }

  static certified(
    state: InvoiceStateModel
  ): Pick<InvoiceStateModel, 'draft'>['draft']['certifiedInvoice'] {
    return state.draft.certifiedInvoice;
  }
}
